/*
 * Self-enrollment — a signed-in user registering their own device.
 *
 * No third party is involved, so this carries none of the admin flow's warnings
 * and none of its delivery affordances: the phone that scans the code is in the
 * same hands as the browser showing it, so there is nothing to copy or send.
 *
 * selfEnroll takes an *empty* body — no orgId, no email, no userId. The session
 * is the identity. The call fires on open, and this is mounted by a click.
 */
import { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { dispatchSelfEnroll } from "../../utils/apiservice";
import { logOutUser } from "../../common";
import { countdown, fmtExpiry } from "./enrollmentCommon";
import OrgAddressHint from "./OrgAddressHint";

export default function SelfEnrollModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true);
    setError("");
    setData(null);

    dispatchSelfEnroll({
      onSuccess: (res) => {
        if (!live) return;
        setLoading(false);
        setData(res);
        setRemaining(countdown(res?.expiresAt));
      },
      onNotFound: () => {
        if (!live) return;
        setLoading(false);
        setError("Your account could not be found in this organisation.");
      },
      onFailure: ({ message: m, isLogout }) => {
        if (!live) return;
        setLoading(false);
        if (isLogout) { logOutUser(); return; }
        setError(m || "Could not generate an enrollment code.");
      },
      onError: ({ message: m }) => {
        if (!live) return;
        setLoading(false);
        setError(m || "Could not generate an enrollment code.");
      },
    });

    return () => { live = false; };
  }, [open]);

  useEffect(() => {
    if (!open || !data?.expiresAt) return;
    const id = setInterval(() => setRemaining(countdown(data.expiresAt)), 60000);
    return () => clearInterval(id);
  }, [open, data?.expiresAt]);

  const close = () => {
    // Drop the token rather than leaving it in state behind a closed modal.
    setData(null);
    setError("");
    setRemaining(null);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
      width={400}
      title={
        <span style={{ fontFamily: AN_FONT, fontSize: 15, fontWeight: 600, color: AN_COLORS.ink }}>
          Set up AuthNull Authenticator
        </span>
      }
    >
      <div style={{ fontFamily: AN_FONT }}>
        {loading && <div style={{ padding: "40px 0", textAlign: "center" }}><Spin /></div>}

        {!loading && error && (
          <div style={{
            padding: "12px 14px", background: "var(--an-dangerBg)", border: "1px solid var(--an-dangerBd)",
            borderRadius: 8, fontSize: 13, color: AN_COLORS.danger,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div style={{ fontSize: 12.5, lineHeight: 1.5, color: AN_COLORS.muted, marginBottom: 16 }}>
              Open the AuthNull Authenticator on your phone and scan this code to register it.
              It expires {fmtExpiry(data.expiresAt)} and works once.
            </div>

            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
              padding: 18, background: "var(--an-surface)", border: `1px solid ${AN_COLORS.line}`, borderRadius: 10,
            }}>
              {data.qrPngBase64 ? (
                // Rendered as returned — the server keeps the QR version low so it
                // scans off a laptop screen at arm's length.
                <img
                  src={`data:image/png;base64,${data.qrPngBase64}`}
                  alt="Your enrollment QR code"
                  style={{ width: "100%", maxWidth: 240, imageRendering: "pixelated" }}
                />
              ) : (
                <div style={{ fontSize: 13, color: AN_COLORS.muted }}>No QR image was returned.</div>
              )}

              <div style={{
                fontFamily: AN_MONO, fontSize: 11, letterSpacing: 0.4,
                color: remaining === "expired" ? AN_COLORS.danger : AN_COLORS.muted,
              }}>
                {remaining === "expired" ? "EXPIRED" : `EXPIRES IN ${remaining || "—"}`}
              </div>
            </div>

            <OrgAddressHint activationCode={data.activationCode} consoleHost={data.consoleHost} compact />

            {(data.orgName || data.orgHost) && (
              <div style={{ marginTop: 12, fontSize: 12, color: AN_COLORS.muted, textAlign: "center" }}>
                {data.orgName}
                {data.orgHost && <span style={{ fontFamily: AN_MONO, fontSize: 11 }}> · {data.orgHost}</span>}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
